// Right-side related-list panel used on detail pages. Mirrors Zoho's detail layout.
import { useState } from "react";
import { cn } from "@/lib/cn";
import {
  StickyNote, Activity as ActivityIcon, CalendarDays, CheckSquare,
  Paperclip, Link as LinkIcon, Tag as TagIcon, Swords, Network,
} from "lucide-react";

export type RelatedTab =
  | "notes"
  | "activities"
  | "meetings"
  | "tasks"
  | "emails"
  | "attachments"
  | "tags"
  | "competitors"
  | "hierarchy";

export interface RelatedListSidebarProps {
  tabs: RelatedTab[];
  active: RelatedTab;
  onSelect: (t: RelatedTab) => void;
  counts?: Partial<Record<RelatedTab, number>>;
}

const TAB_META: Record<RelatedTab, { label: string; icon: any }> = {
  notes:       { label: "Notes",       icon: StickyNote },
  activities:  { label: "Activities",  icon: ActivityIcon },
  meetings:    { label: "Meetings",    icon: CalendarDays },
  tasks:       { label: "Tasks",       icon: CheckSquare },
  emails:      { label: "Emails",      icon: LinkIcon },
  attachments: { label: "Attachments", icon: Paperclip },
  tags:        { label: "Tags",        icon: TagIcon },
  competitors: { label: "Competitors", icon: Swords },
  hierarchy:   { label: "Hierarchy",   icon: Network },
};

export function RelatedListSidebar({ tabs, active, onSelect, counts = {} }: RelatedListSidebarProps) {
  return (
    <aside className="w-48 shrink-0 border-r bg-card">
      <div className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Related
      </div>
      <ul>
        {tabs.map((t) => {
          const meta = TAB_META[t];
          const Icon = meta.icon;
          const n = counts[t];
          const isActive = t === active;
          return (
            <li key={t}>
              <button
                onClick={() => onSelect(t)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-2 text-left text-sm",
                  isActive ? "bg-primary/10 text-primary font-medium" : "text-foreground/80 hover:bg-muted",
                )}
              >
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {meta.label}
                </span>
                {n !== undefined && (
                  <span className={cn("rounded-full px-1.5 text-xs", isActive ? "bg-primary/20" : "bg-muted")}>
                    {n}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

/** Shared inline "notes" tab implementation (posts to /notes polymorphic endpoint). */
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function NotesPanel({ entityType, entityId }: { entityType: "Account" | "Contact" | "Deal" | "Lead" | "JobOrder" | "Candidate" | "GovOpportunity" | "Project" | "Campaign"; entityId: string }) {
  const qc = useQueryClient();
  const queryKey = ["/notes", entityType, entityId];
  const { data } = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get("/notes", { params: { entityType, entityId } })).data.data as any[],
  });

  const [body, setBody] = useState("");
  const addMut = useMutation({
    mutationFn: async () => {
      await api.post("/notes", { entityType, entityId, body });
    },
    onSuccess: () => { setBody(""); qc.invalidateQueries({ queryKey }); },
  });
  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/notes/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  return (
    <div className="space-y-3">
      <div className="rounded-md border bg-card p-3">
        <textarea
          className="min-h-[80px] w-full resize-y rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder="Write a note…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            onClick={() => addMut.mutate()}
            disabled={!body.trim() || addMut.isPending}
          >
            {addMut.isPending ? "Saving…" : "Add note"}
          </Button>
        </div>
      </div>

      {(data?.length ?? 0) === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No notes yet.</p>
      ) : (
        <ul className="space-y-2">
          {data!.map((n) => (
            <li key={n.id} className="rounded-md border bg-card p-3 text-sm">
              <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                <span>{n.user?.name ?? "—"} · {new Date(n.createdAt).toLocaleString()}</span>
                <button
                  onClick={() => delMut.mutate(n.id)}
                  className="text-destructive hover:underline"
                >
                  Delete
                </button>
              </div>
              <p className="whitespace-pre-wrap">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
